import { DestroyRef, Injector, Signal, Type, inject } from "@angular/core";
import { FrontendToolHandlerContext } from "@copilotkit/core";
import type { StandardSchemaV1 } from "@copilotkit/shared";
import { AgentKitService } from "./agent-kit.service";

export type AngularToolCall<Args extends Record<string, unknown> = Record<string, unknown>> =
  | {
      args: Partial<Args>;
      status: "in-progress";
      result: undefined;
    }
  | {
      args: Args;
      status: "executing";
      result: undefined;
    }
  | {
      args: Args;
      status: "complete";
      result: string;
    };

export interface ToolRenderer<Args extends Record<string, unknown> = Record<string, unknown>> {
  toolCall: Signal<AngularToolCall<Args>>;
}

export type ClientTool<Args extends Record<string, unknown> = Record<string, unknown>> = {
  name: string;
  agentId?: string;
  renderer?: Type<ToolRenderer<Args>>;
  parameters?: StandardSchemaV1<any, Args>;
};

export interface RenderToolCallConfig<Args extends Record<string, unknown> = Record<string, unknown>> {
  name: string;
  args: StandardSchemaV1<any, Args>;
  component: Type<ToolRenderer<Args>>;
  agentId?: string;
}

export interface FrontendToolConfig<Args extends Record<string, unknown> = Record<string, unknown>> {
  name: string;
  description: string;
  parameters: StandardSchemaV1<any, Args>;
  component?: Type<ToolRenderer<Args>>;
  handler: (args: Args, context: FrontendToolHandlerContext) => Promise<unknown>;
  agentId?: string;
}

export function registerRenderToolCall<Args extends Record<string, unknown> = Record<string, unknown>>(
  renderToolCall: RenderToolCallConfig<Args>,
): void {
  const agentKit = inject(AgentKitService);
  const destroyRef = inject(DestroyRef);

  agentKit.addRenderToolCall(renderToolCall);

  destroyRef.onDestroy(() => {
    agentKit.removeTool(renderToolCall.name, renderToolCall.agentId);
  });
}

export function registerFrontendTool<Args extends Record<string, unknown> = Record<string, unknown>>(
  frontendTool: FrontendToolConfig<Args>,
): void {
  const injector = inject(Injector);
  const destroyRef = inject(DestroyRef);
  const agentKit = inject(AgentKitService);

  agentKit.addFrontendTool({
    ...(frontendTool as FrontendToolConfig),
    injector,
  });

  destroyRef.onDestroy(() => {
    agentKit.removeTool(frontendTool.name, frontendTool.agentId);
  });
}
